import { useEffect, useState } from "react";
import { DevToolbar } from "./components/DevToolbar";
import { MobileHeader } from "./components/MobileHeader";
import { ReadOnlyOverlay } from "./components/ReadOnlyOverlay";
import { Sidebar } from "./components/Sidebar";
import { isReadOnly } from "./lib/access";
import { supabase } from "./supabaseClient";
import { S, THEME_STORAGE_KEY, applyTheme, initialTheme } from "./theme";
import { AIChat } from "./views/AIChat";
import { AuthScreen } from "./views/AuthScreen";
import { Biomarkers } from "./views/Biomarkers";
import { Blog } from "./views/Blog";
import { Community } from "./views/Community";
import { Dashboard } from "./views/Dashboard";
import { FDAUpdates } from "./views/FDAUpdates";
import { InjectionSites } from "./views/InjectionSites";
import { LandingPage } from "./views/LandingPage";
import { LegalLanding, LegalPage } from "./views/Legal";
import { Library } from "./views/Library";
import { Pricing } from "./views/Pricing";
import { ProtocolBuilder } from "./views/ProtocolBuilder";
import { Settings } from "./views/Settings";
import { Inventory } from "./views/Inventory";
import { TrackToday } from "./views/TrackToday";

export default function App() {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [screen, setScreen] = useState("landing"); // landing | login | signup | app
  const [active, setActive] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [theme, setTheme] = useState(initialTheme);

  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";
    applyTheme(next);
    try { window.localStorage.setItem(THEME_STORAGE_KEY, next); } catch { /* private mode */ }
    setTheme(next);
  }

  // ── Dev toolbar state (localhost only) ──
  const isDev = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  const [devTier, setDevTier] = useState(null); // null = use real auth
  const devOptions = [
    { label: "Real Auth", value: null },
    { label: "Not Logged In", value: "__logged_out__" },
    { label: "New User (no plan)", value: "new_user" },
    { label: "Trialing", value: "trialing" },
    { label: "Expired Trial", value: "expired" },
    { label: "Pro (Monthly)", value: "pro" },
    { label: "Founding Member", value: "founding_member" },
  ];
  const effectiveUser = (() => {
    if (!isDev || devTier === null) return user;
    if (devTier === "__logged_out__") return null;
    const base = { id: "dev-user-000", email: "dev@trackmypeptide.com", name: "Dev User" };
    if (devTier === "new_user") return { ...base, tier: null, subscription_status: null, stripe_customer_id: null };
    if (devTier === "trialing") return { ...base, tier: null, subscription_status: "trialing", stripe_customer_id: null, subscription_current_period_end: new Date(Date.now() + 7 * 86400000).toISOString() };
    if (devTier === "expired") return { ...base, tier: null, subscription_status: "canceled", stripe_customer_id: null };
    if (devTier === "pro") return { ...base, tier: "pro", subscription_status: "active", stripe_customer_id: "cus_dev_test", subscription_current_period_end: new Date(Date.now() + 30 * 86400000).toISOString() };
    if (devTier === "founding_member") return { ...base, tier: "founding_member", subscription_status: null, stripe_customer_id: null };
    return { ...base, tier: devTier, subscription_status: null, stripe_customer_id: null };
  })();

  // Capture the browser's install prompt (Android/Chrome/Edge) and detect iOS
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); setShowInstall(true); };
    window.addEventListener("beforeinstallprompt", handler);
    // iOS Safari doesn't fire the event; show a hint if on iOS and not already installed
    const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isIOS && !standalone && !localStorage.getItem("tmp_install_dismissed")) setShowInstall(true);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  async function doInstall() {
    if (installPrompt) {
      installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
      setShowInstall(false);
    }
  }
  function dismissInstall() {
    setShowInstall(false);
    localStorage.setItem("tmp_install_dismissed", "1");
  }
  const isIOSDevice = /iphone|ipad|ipod/i.test(typeof navigator !== "undefined" ? navigator.userAgent : "");

  const [protocol, setProtocol] = useState([]);
  const [todayLog, setTodayLog] = useState({});
  const [biomarkers, setBiomarkers] = useState([]);
  const [inventory, setInventoryState] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Listen for auth state (login, logout, OAuth redirect, password recovery)
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // When session changes, build the user object and load data
  useEffect(() => {
    if (!session?.user) { setUser(null); return; }
    const su = session.user;
    const name = su.user_metadata?.name || su.user_metadata?.full_name || su.email.split("@")[0];
    const u = { id: su.id, email: su.email, name, tier: null, stripe_customer_id: null, subscription_status: null };
    setUser(u);
    setScreen("app");
    loadUserData(su.id);
    // Ensure a profile row exists (for Google sign-ins)
    supabase.from("profiles").upsert({ id: su.id, name, email: su.email }, { onConflict: "id" }).then(() => {});
    // Load tier and subscription info into user state
    loadUserTier(su.id);
  }, [session]);

  // Load tier + Stripe customer info from profiles table
  async function loadUserTier(uid) {
    const { data, error } = await supabase
      .from("profiles")
      .select("tier, stripe_customer_id, subscription_status, subscription_current_period_end")
      .eq("id", uid)
      .single();
    if (error) {
      console.error("Failed to load user tier:", error);
      return;
    }
    setUser(prev => prev ? {
      ...prev,
      tier: data?.tier || null,
      stripe_customer_id: data?.stripe_customer_id || null,
      subscription_status: data?.subscription_status || null,
      subscription_current_period_end: data?.subscription_current_period_end || null,
    } : prev);
  }

  // Handle redirect-back from Stripe Checkout (success or cancel)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const sub = params.get("subscription");
    if (sub === "success") {
      // Stripe webhook will update the DB; we refetch a bit later to give it time
      setTimeout(() => { if (user?.id) loadUserTier(user.id); }, 2500);
      // Strip the query params so refresh doesn't re-trigger
      const url = window.location.pathname;
      window.history.replaceState({}, "", url);
      alert("Welcome to Pro 🌿\n\nYour account is active. Thank you for being here.");
    } else if (sub === "canceled") {
      const url = window.location.pathname;
      window.history.replaceState({}, "", url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  async function loadUserData(uid) {
    setDataLoading(true);
    try {
      const { data: protos } = await supabase.from("protocols").select("*").eq("user_id", uid).order("created_at");
      setProtocol((protos || []).map(p => ({
        rowId: p.id, id: p.peptide_id, name: p.name, category: p.category, color: p.color,
        dose: p.dose, route: p.route, frequency: p.frequency, notes: p.notes,
        summary: p.summary, dosing: p.dosing, cycle: p.cycle, startDate: p.start_date,
      })));

      const today = new Date().toDateString();
      const { data: logs } = await supabase.from("daily_logs").select("*").eq("user_id", uid).eq("log_date", today);
      const logMap = {};
      (logs || []).forEach(l => { logMap[l.protocol_id] = l.checked; });
      setTodayLog(logMap);

      const { data: bios } = await supabase.from("biomarkers").select("*").eq("user_id", uid).order("created_at", { ascending: false });
      setBiomarkers((bios || []).map(b => ({
        id: b.id, marker: b.marker, value: parseFloat(b.value), unit: b.unit, note: b.note,
        date: b.logged_date, timestamp: new Date(b.created_at).getTime(),
      })));

      const { data: inv } = await supabase.from("inventory").select("*").eq("user_id", uid);
      setInventoryState(inv || []);
    } catch (e) { /* table may be empty */ }
    setDataLoading(false);
  }

  // ── Protocol add/remove (writes to DB)
  async function addProtocol(p) {
    if (!user) return;
    const { data } = await supabase.from("protocols").insert({
      user_id: user.id, peptide_id: p.id, name: p.name, category: p.category, color: p.color,
      dose: p.dose, route: p.route, frequency: p.frequency, notes: p.notes || "",
      summary: p.summary, dosing: p.dosing, cycle: p.cycle, start_date: p.startDate,
    }).select().single();
    if (data) setProtocol(prev => [...prev, { ...p, rowId: data.id }]);
  }
  async function removeProtocol(peptideId) {
    if (!user) return;
    const row = protocol.find(p => p.id === peptideId);
    if (row?.rowId) await supabase.from("protocols").delete().eq("id", row.rowId);
    setProtocol(prev => prev.filter(p => p.id !== peptideId));
  }
  // setProtocol-compatible wrapper for child components
  function protocolSetter(updater) {
    const next = typeof updater === "function" ? updater(protocol) : updater;
    // Figure out add vs remove by comparing
    if (next.length > protocol.length) {
      const added = next.find(n => !protocol.some(p => p.id === n.id));
      if (added) addProtocol(added);
    } else if (next.length < protocol.length) {
      const removed = protocol.find(p => !next.some(n => n.id === p.id));
      if (removed) removeProtocol(removed.id);
    } else {
      setProtocol(next); // edits in place
    }
  }

  // ── Daily log toggle (writes to DB)
  async function setTodayLogWrapper(updater, injectionSiteId) {
    const next = typeof updater === "function" ? updater(todayLog) : updater;
    setTodayLog(next);
    if (!user) return;
    const today = new Date().toDateString();
    // Find which key changed
    const changedKey = Object.keys(next).find(k => next[k] !== todayLog[k]) ||
                       Object.keys(todayLog).find(k => next[k] !== todayLog[k]);
    if (!changedKey) return;
    const isChecked = next[changedKey];
    if (isChecked) {
      const row = { user_id: user.id, log_date: today, protocol_id: changedKey, checked: true };
      // Only set the column when an injection flow provides it, so plain
      // (non-injectable) check-offs don't depend on the linked injection.
      if (injectionSiteId !== undefined) row.injection_site_id = injectionSiteId;
      await supabase.from("daily_logs").upsert(row, { onConflict: "user_id,log_date,protocol_id" });
      // Auto-deduct from inventory
      const inv = inventory.find(i => i.peptide_id === changedKey);
      if (inv) {
        const updated = { ...inv, doses_used: (inv.doses_used || 0) + 1 };
        setInventoryWrapper(updated);
      }
    } else {
      await supabase.from("daily_logs").delete().eq("user_id", user.id).eq("log_date", today).eq("protocol_id", changedKey);
    }
  }

  // ── Biomarkers (writes to DB)
  async function setBiomarkersWrapper(updater) {
    const next = typeof updater === "function" ? updater(biomarkers) : updater;
    if (next.length > biomarkers.length && user) {
      const added = next[0];
      const { data } = await supabase.from("biomarkers").insert({
        user_id: user.id, marker: added.marker, value: added.value, unit: added.unit,
        note: added.note, logged_date: added.date,
      }).select().single();
      if (data) { added.id = data.id; }
    }
    setBiomarkers(next);
  }

  // ── Inventory upsert (writes to DB)
  async function setInventoryWrapper(entry) {
    if (!user) return;
    const row = { user_id: user.id, ...entry };
    delete row.id; delete row.created_at; delete row.updated_at;
    row.updated_at = new Date().toISOString();
    const { data } = await supabase.from("inventory")
      .upsert(row, { onConflict: "user_id,peptide_id" })
      .select().single();
    if (data) {
      setInventoryState(prev => {
        const idx = prev.findIndex(i => i.peptide_id === data.peptide_id);
        if (idx >= 0) { const next = [...prev]; next[idx] = data; return next; }
        return [...prev, data];
      });
    }
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProtocol([]); setTodayLog({}); setBiomarkers([]); setInventoryState([]);
    setScreen("landing");
  }

  if (authLoading) {
    return <div style={{ minHeight: "100vh", background: S.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Fraunces', serif", fontWeight: 700, color: S.muted }}>Loading…</div>;
  }

  // Skip landing page guard if dev toolbar has a tier override active
  const devOverrideActive = isDev && devTier !== null && devTier !== "__logged_out__";
  if (!user && !devOverrideActive) {
    // Dev "Not Logged In" mode — show landing with toolbar
    if (isDev && devTier === "__logged_out__") {
      return (
        <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
          <DevToolbar devTier={devTier} setDevTier={setDevTier} devOptions={devOptions} />
          <LandingPage onGetStarted={(mode) => setScreen(mode)} viewLegal={(key) => setScreen("legal-" + key)} />
        </div>
      );
    }
    if (screen === "signup" || screen === "login") return <AuthScreen initialMode={screen} />;
    if (screen === "legal-terms") return <LegalLanding which="terms" onBack={() => setScreen("landing")} />;
    if (screen === "legal-privacy") return <LegalLanding which="privacy" onBack={() => setScreen("landing")} />;
    if (screen === "legal-disclaimer") return <LegalLanding which="disclaimer" onBack={() => setScreen("landing")} />;
    return <LandingPage onGetStarted={(mode) => setScreen(mode)} viewLegal={(key) => setScreen("legal-" + key)} />;
  }

  const viewUser = effectiveUser || user;
  const locked = isReadOnly(viewUser);

  // Wrapper: show read-only overlay instead of the real screen when locked
  const gated = (component) => locked ? <ReadOnlyOverlay setActive={setActive} /> : component;

  const screens = {
    dashboard: <Dashboard user={viewUser} protocol={protocol} todayLog={todayLog} setActive={setActive} inventory={inventory} />,
    protocol: gated(<ProtocolBuilder protocol={protocol} setProtocol={protocolSetter} />),
    track: gated(<TrackToday protocol={protocol} todayLog={todayLog} setTodayLog={setTodayLogWrapper} user={viewUser} />),
    biomarkers: gated(<Biomarkers biomarkers={biomarkers} setBiomarkers={setBiomarkersWrapper} />),
    injections: gated(<InjectionSites user={viewUser} protocol={protocol} />),
    inventory: gated(<Inventory protocol={protocol.map(p => ({ ...p, peptide_id: p.id }))} inventory={inventory} setInventory={setInventoryWrapper} />),
    library: <Library protocol={protocol} setProtocol={protocolSetter} setActive={setActive} />,
    ai: gated(<AIChat protocol={protocol} />),
    community: <Community user={viewUser} />,
    blog: <Blog setActive={setActive} />,
    fda: <FDAUpdates />,
    pricing: <Pricing user={viewUser} />,
    settings: <Settings user={viewUser} />,
    terms: <LegalPage which="terms" setActive={setActive} />,
    privacy: <LegalPage which="privacy" setActive={setActive} />,
    disclaimer: <LegalPage which="disclaimer" setActive={setActive} />,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
      {isDev && <DevToolbar devTier={devTier} setDevTier={setDevTier} devOptions={devOptions} />}
      {showInstall && (
        <div style={{ background: S.darkPanel, color: "#fff", padding: "12px 18px", display: "flex", alignItems: "center", gap: 14, justifyContent: "center", flexWrap: "wrap", fontSize: "0.85rem" }}>
          <span style={{ fontWeight: 600 }}>📲 Add TrackMyPeptide to your home screen</span>
          {isIOSDevice ? (
            <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem" }}>
              Tap the Share button, then "Add to Home Screen"
            </span>
          ) : (
            <button onClick={doInstall} style={{ background: S.accent, color: "#fff", border: "none", borderRadius: 100, padding: "7px 18px", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", fontFamily: "'Inter', sans-serif" }}>
              Install app
            </button>
          )}
          <button onClick={dismissInstall} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer", fontSize: "1.1rem", lineHeight: 1, padding: "0 4px" }}>×</button>
        </div>
      )}
      <MobileHeader setMobileOpen={setMobileOpen} active={active} />
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Sidebar active={active} setActive={setActive} user={viewUser} onLogout={handleLogout} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} theme={theme} toggleTheme={toggleTheme} />
        <main style={{ flex: 1, background: S.bg, overflowY: "auto", minWidth: 0 }}>
          {screens[active]}
        </main>
      </div>
      <style>{`
        /* Default desktop sidebar */
        .tmp-sidebar { width: 240px; min-height: 100vh; }
        .tmp-sidebar-close { display: none; }
        .tmp-mobile-header { display: none !important; }

        /* Tablet & mobile */
        @media (max-width: 900px) {
          .tmp-mobile-header { display: flex !important; }
          .tmp-sidebar {
            position: fixed;
            top: 0; left: 0; bottom: 0;
            width: 280px;
            min-height: 100vh;
            transform: translateX(${mobileOpen ? "0" : "-100%"});
            transition: transform 0.25s ease;
            z-index: 60;
            box-shadow: 0 0 40px rgba(0,0,0,0.3);
          }
          .tmp-sidebar-close { display: block !important; }

          main > div { padding: 24px 18px !important; }
          main h1, main h2 { font-size: 1.5rem !important; line-height: 1.15 !important; }
          main h1 br, main h2 br { display: none; }

          /* Force grids to stack on mobile */
          main div[style*="grid-template-columns: repeat(4"],
          main div[style*="grid-template-columns: repeat(3"],
          main div[style*="grid-template-columns: 1fr 1fr"],
          main div[style*="grid-template-columns: 2fr 1fr"],
          main div[style*="grid-template-columns: repeat(auto-fill"] {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }

          /* Two-column grids stay 2 wide for stats */
          main div[style*="grid-template-columns: repeat(4,1fr)"] {
            grid-template-columns: 1fr 1fr !important;
          }

          /* Flex rows wrap on mobile */
          main div[style*="display: \"flex\""] {
            flex-wrap: wrap;
          }

          /* Cards have less padding */
          main div[style*="padding: 28px"],
          main div[style*="padding: 32px"],
          main div[style*="padding: 40px"],
          main div[style*="padding: 48px"] {
            padding: 20px !important;
          }

          /* AI chat input row stacks */
          main input[type="text"], main input[type="email"], main input[type="password"], main textarea {
            font-size: 16px !important; /* prevents iOS zoom on focus */
          }

          /* Landing page hero stacks */
          section[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
        }

        /* Very narrow phones */
        @media (max-width: 480px) {
          main > div { padding: 18px 14px !important; }
          main h1, main h2 { font-size: 1.3rem !important; }
        }
      `}</style>
    </div>
  );
}
